import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import { OmniCluster } from '@cast/types-omni';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const OMNI_CLUSTERS_QUERY_KEY = 'omni-clusters';

const getOmniClustersQueryKey = (includeEdgeLocations: boolean) =>
  [OMNI_CLUSTERS_QUERY_KEY, includeEdgeLocations] as const;

const getOmniClustersQueryOptions = ({
  includeEdgeLocations,
  refetchInterval,
  enabled,
  organizationId,
}: {
  includeEdgeLocations: boolean;
  refetchInterval: QueryRefetchInterval<OmniCluster[]>;
  enabled: boolean;
  organizationId: string | undefined;
}) =>
  queryOptions({
    queryKey: getOmniClustersQueryKey(includeEdgeLocations),
    queryFn: async () => {
      const { data } = await apiClient.omni.listClusters({
        organizationId: organizationId!,
        includeEdgeLocations,
      });

      return data.items ?? [];
    },
    refetchInterval,
    enabled,
  });

export const useListOmniClustersQuery = ({
  includeEdgeLocations = false,
  refetchInterval = false,
  enabled = true,
}: {
  includeEdgeLocations?: boolean;
  refetchInterval?: QueryRefetchInterval<OmniCluster[]>;
  enabled?: boolean;
} = {}) => {
  const { currentOrganization } = useOrganizations();

  const { data, isLoading, error, refetch } = useQuery(
    getOmniClustersQueryOptions({
      includeEdgeLocations,
      refetchInterval,
      enabled,
      organizationId: currentOrganization?.id,
    })
  );

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
